import { ConvexHttpClient, type ConvexClient } from "convex/browser";
import { makeFunctionReference } from "convex/server";
import { CANVAS_ACT_HEADER } from "@cleo/shared/canvasDoorPress";
import { ConvexError } from "convex/values";

/**
 * The window's own call to the World's one `/mcp` door (steps 5-7 design §12, §12a; step 7.4).
 *
 * The Desk's server calls `/mcp` with the grants the Desk's sign-in deposited (apps/server worldToolTransport.ts). A window
 * with no Mac behind it (cleo-web; later the Desk's window and Pocket) calls the same door, as the same person, with a grant
 * of its own (backend windowGrants.js): minted and renewed through the window's Clerk session (its World token is a JWT
 * template with no session id, so the window also sends its own Clerk session token: publishWindowGrantHost), five minutes long, the
 * Canvas door and the Inbox's calls, and held here in memory alone. It is never stored, never logged, never given to a Canvas
 * frame, and never sent anywhere but the World that minted it: its `mcpUrl`, and its notification watch on this window's own
 * connection to that World (windowGrantBearer). The World's own checks decide every call.
 *
 * 7.4.1 uses it for the Canvas door's reads only (WINDOW_CANVAS_READS); writes follow in 7.4.2-7.4.4, each with the parts
 * the Desk's server adds today (retain, the gesture's gate, the press header).
 */

type WindowGrant = {
  readonly grantId: string;
  readonly mcpUrl: string;
  readonly token: string;
  readonly expiresAt: number;
  readonly worldKey: string;
};

const mintRef = makeFunctionReference<
  "action",
  { worldKey: string; sessionToken: string },
  WindowGrant & { tools: string[] }
>("windowGrants:mint");
const renewRef = makeFunctionReference<
  "action",
  { grantId: string; sessionToken: string },
  { expiresAt: number }
>("windowGrants:renew");
const revokeRef = makeFunctionReference<"mutation", { token: string }, null>(
  "windowGrants:revokeByBearer",
);

/** What the window's sign-in gives this module: its own Clerk session token, and the World to end a grant at. */
export type WindowGrantHost = {
  readonly sessionToken: () => Promise<string | null>;
  readonly convexUrl: string;
};
let host: WindowGrantHost | null = null;
/** Published by the window's World sign-in (WebHostWorldSession.tsx); null when it signs out. */
export function publishWindowGrantHost(next: WindowGrantHost | null): void {
  if (next === null && host !== null) void dropWindowGrants(null);
  host = next;
}
async function sessionToken(): Promise<string> {
  const token = host ? await host.sessionToken() : null;
  if (!token) throw new Error("This window is not signed in to the World.");
  return token;
}
/** Ends a grant by its bearer, with no Clerk session needed (a signed-out window still ends what it held). */
function endGrant(grant: WindowGrant, convexUrl = host?.convexUrl): void {
  if (!convexUrl) return;
  void new ConvexHttpClient(convexUrl)
    .mutation(revokeRef, { token: grant.token })
    .catch(() => undefined);
}

/** Renewed when less than this is left, so a call never starts on a grant about to lapse. */
const RENEW_WITHIN_MS = 2 * 60 * 1000;
const CALL_TIMEOUT_MS = 30_000;

/**
 * The tools a window grant holds (backend windowGrants.js WINDOW_GRANT_TOOLS): the Canvas door, the gesture's three calls,
 * and the Inbox's list (7.4.4a), whose decide is the gesture's own decide tool.
 */
export type WindowTool =
  | "neucleos_canvas"
  | "neucleos_read_process_binding"
  | "neucleos_open_gate_instance"
  | "neucleos_decide_gate_instance"
  | "neucleos_list_gate_instances"
  // 7.4.4a-2a: the Inbox's read marks, saved and read back as the Desk's notifications/read route does.
  | "neucleos_save_world_view_prefs"
  | "neucleos_read_world_view_prefs";

/**
 * 7.4.2: the Canvas writes of the share sheet a window sends, each as the person's own press (retain, the gesture, then the
 * write: @cleo/shared/canvasPersonWrite). Publishing and friend links (the host bundle) are 7.4.3; switches are 7.4.4.
 */
export const WINDOW_CANVAS_WRITES = Object.freeze([
  "canvas-share",
  "canvas-share-snapshot",
  "canvas-share-revoke",
  "canvas-offer",
  "canvas-offer-withdraw",
  "group-create",
  "group-member-add",
  "group-member-remove",
  "group-retire",
  // The sheet hides a finished request from the person's recovery list once its write is answered (Codex r1 of #2902):
  // an attested press of its own, so the sheet can finish and refresh.
  "request-acknowledge",
  // The sheet's other two withdrawals (Codex review of #2902's merged diff): stop sharing a friend link (retained and
  // attested, like a revoke; it needs no host bundle) and withdraw a reader dataset (attested).
  "canvas-friend-link-remove",
  "dataset-revoke",
  // 7.4.3: publishing and a friend link, now that the World holds the public viewer itself (backend canvasHostBundles.js),
  // and taking a page down. The window sends no viewer; the World fills it.
  "canvas-public-publish",
  "canvas-friend-link",
  "canvas-unpublish",
] as const);
export type WindowCanvasWrite = (typeof WINDOW_CANVAS_WRITES)[number];

/** The writes that make a website release: attested on their exact authored call, and given the World's two minutes. */
export const WINDOW_PUBLISH_WRITES: ReadonlyArray<string> = Object.freeze([
  "canvas-public-publish",
  "canvas-friend-link",
]);
export const WEBSITE_RELEASE_TIMEOUT_MS = 120_000;
/** The writes that wait on a website release: publishing, a friend link and taking a page down (Codex review of #2929). */
export const WINDOW_RELEASE_WRITES: ReadonlyArray<string> = Object.freeze([
  "canvas-public-publish",
  "canvas-friend-link",
  "canvas-unpublish",
]);

/** The Canvas door's actions a window may send as reads (the World answers each without a write). */
export const WINDOW_CANVAS_READS = Object.freeze([
  "sharing-audience",
  "canvas-sharing",
  "canvas-dependencies",
  "canvas-friend-links",
  "canvas-publications",
  "canvas-offers",
  "canvas-offers-received",
  "canvas-shared-in",
  "canvas-datasets",
  "group-list",
  "group-read",
  "action-history",
  "action-request",
  "request-list",
  "request-read",
  // 7.4.3: the Publish sheet's reads (its review, the person's websites, a path's current page).
  "canvas-public-review",
  "canvas-public-sites",
  "canvas-public-target",
  // The Publish sheet's delegation reviews: a live dataset and a public Form's submissions (Codex review of #2929).
  "dataset-catalog",
  "submission-catalog",
] as const);
export type WindowCanvasRead = (typeof WINDOW_CANVAS_READS)[number];

type Held = { owner: string; grant: WindowGrant };
const held = new Map<string, Held>();
const minting = new Map<string, Promise<WindowGrant>>();
// Moves on every drop (sign-out, a change of person): a mint or renewal that started before it never lands after it.
let generation = 0;

/** The person this window is signed in as, for holding grants per person (a change of person drops the old ones). */
const DROPPED = "This window's sign-in changed while the World was asked; nothing was sent.";
const DROPPED_ANSWER =
  "This window's sign-in changed while the World answered; the answer was not shown.";

/**
 * The World's own words for a refused mint or renewal. A deployed World redacts a thrown error's message ("Server Error"),
 * so its refusal is read from the ConvexError's data first (Codex r2 of #2949), and the message only when there is none.
 */
export function worldErrorWords(error: unknown): string {
  if (error instanceof ConvexError) {
    const data: unknown = error.data;
    if (typeof data === "string") return data;
    if (
      typeof data === "object" &&
      data !== null &&
      typeof (data as { message?: unknown }).message === "string"
    )
      return (data as { message: string }).message;
  }
  return error instanceof Error
    ? error.message.replace(/^\[CONVEX[^\]]*\]\s*/u, "")
    : String(error);
}

/** Forgets a grant only if it is still the one held for that World (a later grant is never dropped by an earlier call). */
function forget(worldKey: string, grantId: string): void {
  if (held.get(worldKey)?.grant.grantId === grantId) held.delete(worldKey);
}

async function ownGrant(
  client: ConvexClient,
  owner: string,
  worldKey: string,
  now = Date.now(),
): Promise<WindowGrant> {
  const started = generation;
  const current = held.get(worldKey);
  if (current && current.owner !== owner) {
    held.delete(worldKey);
    endGrant(current.grant);
  } else if (current && current.grant.expiresAt - now > RENEW_WITHIN_MS) {
    return current.grant;
  } else if (current && current.grant.expiresAt > now) {
    try {
      const { expiresAt } = await client.action(renewRef, {
        grantId: current.grant.grantId,
        sessionToken: await sessionToken(),
      });
      // Dropped while renewing: the grant is no longer this window's to use.
      if (generation !== started || held.get(worldKey)?.grant.grantId !== current.grant.grantId)
        throw new Error(DROPPED);
      const renewed = { ...current.grant, expiresAt };
      held.set(worldKey, { owner, grant: renewed });
      return renewed;
    } catch (error) {
      if (error instanceof Error && error.message === DROPPED) throw error;
      forget(worldKey, current.grant.grantId);
    }
  } else if (current) {
    held.delete(worldKey);
  }
  const key = `${generation}\u0000${owner}\u0000${worldKey}`;
  const pending =
    minting.get(key) ??
    (async () => {
      try {
        const convexUrl = host?.convexUrl;
        const minted = await client.action(mintRef, {
          worldKey,
          sessionToken: await sessionToken(),
        });
        const grant: WindowGrant = {
          grantId: minted.grantId,
          mcpUrl: minted.mcpUrl,
          token: minted.token,
          expiresAt: minted.expiresAt,
          worldKey: minted.worldKey,
        };
        // A mint that lands after a sign-out or a change of person is ended at once and never used.
        if (generation !== started) {
          endGrant(grant, convexUrl);
          throw new Error(DROPPED);
        }
        held.set(worldKey, { owner, grant });
        return grant;
      } finally {
        minting.delete(key);
      }
    })();
  minting.set(key, pending);
  return pending;
}

/**
 * 7.4.4a: the window's grant for a World, for the one other place the World reads it: its notification watch
 * (backend worldNotifications.js watch, the query the Desk's server watches with its own grant), subscribed on this
 * window's own connection to the same World. Renewed as a call would renew it; `forgetWindowGrant` drops one the World
 * no longer answers to, so the next read mints again.
 */
export async function windowGrantBearer(input: {
  readonly client: ConvexClient;
  readonly owner: string;
  readonly worldKey: string;
}): Promise<
  | {
      readonly ok: true;
      readonly grantId: string;
      readonly token: string;
      readonly expiresAt: number;
    }
  | {
      readonly ok: false;
      readonly kind: "upstream_refusal" | "not_signed_in";
      readonly reason: string;
    }
> {
  const started = generation;
  try {
    const grant = await ownGrant(input.client, input.owner, input.worldKey);
    if (generation !== started) return { ok: false, kind: "not_signed_in", reason: DROPPED };
    return { ok: true, grantId: grant.grantId, token: grant.token, expiresAt: grant.expiresAt };
  } catch (error) {
    const message = worldErrorWords(error);
    return {
      ok: false,
      kind: /sign in|signed in|unauthenticated|ended/iu.test(message)
        ? "not_signed_in"
        : "upstream_refusal",
      reason: message.slice(0, 400),
    };
  }
}

/** Forgets the window's grant for a World when it is still that grant (the World no longer answers to it). */
export function forgetWindowGrant(worldKey: string, grantId: string): void {
  forget(worldKey, grantId);
}

/** How long before a grant's end the window renews it (a watch renews on this schedule). */
export const WINDOW_GRANT_RENEW_WITHIN_MS = RENEW_WITHIN_MS;

/**
 * Ends and forgets every grant this window holds (sign-out, a change of person), by bearer, so no Clerk session is needed;
 * a mint or renewal still under way is ended when it lands. Best effort: each also ends with its Clerk session, and in five
 * minutes at the latest.
 */
export async function dropWindowGrants(_client?: ConvexClient | null): Promise<void> {
  generation += 1;
  const all = [...held.values()];
  held.clear();
  minting.clear();
  for (const { grant } of all) endGrant(grant);
}

export type WorldMcpOutcome =
  | { readonly ok: true; readonly value: unknown }
  | {
      readonly ok: false;
      readonly kind: "upstream_refusal" | "transport" | "not_signed_in";
      readonly reason: string;
    };

const failed = (
  kind: "upstream_refusal" | "transport" | "not_signed_in",
  reason: string,
): WorldMcpOutcome => ({
  ok: false,
  kind,
  reason: reason.slice(0, 400),
});

/** One reply of the door, read as the Desk's server reads it (worldToolTransport.ts): a refusal, a structured value, or its JSON text. */
export function outcomeOfReply(text: string, token: string): WorldMcpOutcome {
  if (text.includes(token))
    return failed("transport", "The World returned an unsafe tool response.");
  let envelope: unknown;
  try {
    envelope = JSON.parse(text);
  } catch {
    return failed("transport", "The World door returned an unreadable answer.");
  }
  const rpc = envelope as {
    jsonrpc?: unknown;
    id?: unknown;
    result?: unknown;
    error?: { message?: unknown };
  };
  if (!rpc || typeof rpc !== "object" || rpc.jsonrpc !== "2.0" || rpc.id !== 1)
    return failed("transport", "The World returned an invalid tool response.");
  if (rpc.error !== undefined)
    return failed(
      "upstream_refusal",
      typeof rpc.error?.message === "string" ? rpc.error.message : "The World door refused.",
    );
  const result = rpc.result as
    | {
        isError?: unknown;
        structuredContent?: unknown;
        content?: Array<{ type?: unknown; text?: unknown }>;
      }
    | undefined;
  if (!result || typeof result !== "object")
    return failed("transport", "The World door returned an empty answer.");
  const textOf = () =>
    result.content?.find((item) => item?.type === "text" && typeof item.text === "string")?.text as
      | string
      | undefined;
  if (result.isError === true)
    return failed("upstream_refusal", textOf() ?? "The World door refused the call.");
  if (result.structuredContent !== undefined) return { ok: true, value: result.structuredContent };
  const body = textOf();
  if (body === undefined)
    return failed("transport", "The World door returned a tool result with no content.");
  try {
    return { ok: true, value: JSON.parse(body) };
  } catch {
    return failed("transport", "The World door returned an unreadable tool result.");
  }
}

/** Calls one tool of the World's door as the person signed in to this window. */
export async function callWorldMcp(input: {
  readonly client: ConvexClient;
  readonly owner: string;
  readonly worldKey: string;
  readonly tool: WindowTool;
  readonly args: Record<string, unknown>;
  readonly fetchImpl?: typeof fetch;
  /** A website release may take up to two minutes at the World (the Desk's WEBSITE_RELEASE_TIMEOUT_MS). */
  readonly timeoutMs?: number;
  /** 7.4.4b-1b: a Canvas switch's press, sent as the x-neucleos-canvas-act header (@cleo/shared/canvasDoorPress). */
  readonly canvasAct?: string;
}): Promise<WorldMcpOutcome> {
  // The call belongs to the sign-in it started in: a drop (sign-out, another person) at any await below stops it before it
  // sends, and withholds an answer that arrives after it.
  const started = generation;
  let grant: WindowGrant;
  try {
    grant = await ownGrant(input.client, input.owner, input.worldKey);
  } catch (error) {
    const message = worldErrorWords(error);
    return failed(
      /sign in|signed in|unauthenticated|ended/iu.test(message)
        ? "not_signed_in"
        : "upstream_refusal",
      message,
    );
  }
  if (generation !== started) return failed("not_signed_in", DROPPED);
  let response: Response;
  try {
    response = await (input.fetchImpl ?? fetch)(grant.mcpUrl, {
      method: "POST",
      credentials: "omit",
      headers: {
        "content-type": "application/json",
        accept: "application/json",
        authorization: `Bearer ${grant.token}`,
        ...(input.canvasAct !== undefined ? { [CANVAS_ACT_HEADER]: input.canvasAct } : {}),
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "tools/call",
        params: { name: input.tool, arguments: input.args },
      }),
      signal: AbortSignal.timeout(input.timeoutMs ?? CALL_TIMEOUT_MS),
    });
  } catch {
    return failed("transport", "The World did not answer.");
  }
  if (response.status === 401) {
    // The grant ended (its Clerk session, a Revoke, expiry): forget it; the next call mints again or says why it cannot.
    forget(input.worldKey, grant.grantId);
    return failed("not_signed_in", "This window's sign-in to the World has ended.");
  }
  const text = await response.text().catch(() => "");
  if (generation !== started) return failed("not_signed_in", DROPPED_ANSWER);
  if (!response.ok && !text)
    return failed("transport", `The World door answered ${response.status}.`);
  return outcomeOfReply(text, grant.token);
}
